import { UserDto } from "../interfaces/UserInterfaces";
import { CourtDto, FullCourtDto, CreateCourtDto, CreateProsecutorDto, FullProsecutorDto, SearchFilters } from "../interfaces/CourtInterfaces";

export interface GraphAuthStatusInfo {
  status: 'connected' | 'expiring_soon' | 'expired' | 'not_connected' | 'reauth_required' | 'config_error';
  connected: boolean;
  message: string;
  accountEmail: string | null;
  accountName: string | null;
  expiresAt: string | null;
  expiresInMinutes: number | null;
  lastAuthorizedAt: string | null;
  scopes: string[];
  graphVerified: boolean;
  canAutoRefresh: boolean;
}

// A Dialpad call that could not be filed automatically — see backend/src/routes/Calls/UnmatchedCallRouter.ts.
export interface UnmatchedCallDto {
  UnmatchedCall_guid: string;
  UnmatchedCall_Direction: 'inbound' | 'outbound';
  UnmatchedCall_Outcome: 'spoke' | 'voicemail' | 'no-answer';
  UnmatchedCall_ExternalNumber: string;
  // 'no-client': the number matched no client. 'ambiguous-client': it matched more than one —
  // filing automatically risked putting the call summary in the wrong person's legal file.
  UnmatchedCall_Reason: 'no-client' | 'ambiguous-client';
  UnmatchedCall_StartedAt: string;
  UnmatchedCall_DurationSeconds: number | null;
  UnmatchedCall_Summary: string | null;
  UnmatchedCall_RecordingUrl: string | null;
}

export interface UserNoteDto {
  UserNote_guid: string;
  UserNote_Title: string;
  UserNote_Desc: string;
  UserNote_AuthorName: string;
  UserNote_CreatedAt: string;
  // Reads null even when a Ticket_guid was supplied and successfully attached — a pre-existing
  // gap in UserNoteController.asDto (the relation isn't populated on a freshly-created entity).
  // Do not use this field to confirm what was attached.
  Ticket_guid: string | null;
  UserNote_Kind: string;
  UserNote_CallDirection: string | null;
  UserNote_CallOutcome: string | null;
  UserNote_Source: string | null;
  UserNote_CallDurationSeconds: number | null;
  UserNote_CallRecordingUrl: string | null;
  UserNote_CallStartedAt: string | null;
}

export interface AttachUnmatchedCallResult {
  userNote: UserNoteDto;
  unmatchedCall: UnmatchedCallDto;
}

// Thrown by the unmatched-call methods below so callers can branch on HTTP status — in
// particular 409 (row already resolved/dismissed by someone else), which needs different
// handling than a generic failure.
export class ManagerApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ManagerApiError';
    this.status = status;
  }
}

class ManagerApi {
  static async getEmailTokenUrl(): Promise<string> {
    const response = await fetch((process.env.API_HOST || '') + '/api/v1/manager/email-token-url', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for authentication
    });

    if (!response.ok) {
      throw new Error('Failed to get email token URL');
    }

    const data = await response.json();
    return data.url;
  }

  static async setEmailToken(code: string): Promise<void> {
    const response = await fetch((process.env.API_HOST || '') + '/api/v1/manager/set-email-token?code=' + encodeURIComponent(code), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for authentication
    });

    if (!response.ok) {
      throw new Error('Failed to set email token');
    }
  }

  static async getGraphAuthStatus(): Promise<GraphAuthStatusInfo> {
    const response = await fetch((process.env.API_HOST || '') + '/api/v1/manager/graph-auth-status', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to get Microsoft Graph auth status');
    }

    return response.json();
  }

  static async clearGraphAuth(): Promise<GraphAuthStatusInfo> {
    const response = await fetch((process.env.API_HOST || '') + '/api/v1/manager/clear-graph-auth', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to disconnect Microsoft Graph');
    }

    const data = await response.json();
    return data.status;
  }

  static async createUser(userData: {
    // User_Password: string;
    User_Email?: string; // Optional — clients arrive without an address on file
    User_FirstName: string;
    User_MiddleName?: string;
    User_LastName: string;
    User_DOB?: string;
    User_PhoneNumber?: string;
    User_DriversLicenseNumber?: string;
    User_LicenseType?: string;
    User_Source?: string;
  }): Promise<UserDto> {
    // return Promise.resolve({ User_guid: 'test-guid', User_Email: userData.User_Email, User_FirstName: userData.User_FirstName, User_MiddleName: userData.User_MiddleName || '', User_LastName: userData.User_LastName, User_PhoneNumber: userData.User_PhoneNumber || null, User_Active: true, User_DOB: null });
    const response = await fetch((process.env.API_HOST || '') + '/api/v1/manager/manager-create-user', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for authentication
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({} as { message?: string; errors?: Array<{ msg?: string }> }));
      // "Validation failed" on its own tells staff nothing about which field the server rejected,
      // so the per-field messages are carried through with it.
      const fieldErrors = Array.isArray(errorData.errors)
        ? errorData.errors.map((e: { msg?: string }) => e.msg).filter(Boolean)
        : [];
      const detail = fieldErrors.length > 0 ? `: ${fieldErrors.join('; ')}` : '';
      throw new Error((errorData.message || 'Failed to create user') + detail);
    }

    const data = await response.json();
    return data;
  }

  /*
   * The server decides how the reset reaches the client, and says which it used.
   *
   * `link` is the normal path: a single-use link goes to the client's own address and the typed
   * password is discarded. `on-screen` happens only when the client has no address on file — the
   * password is set and comes back here to be read out, never emailed. Callers must show the
   * returned message rather than assuming an email was sent, because for an address-less client
   * nothing is sent at all.
   */
  static async resetUserPassword(
    userGuid: string,
    newPassword: string,
  ): Promise<{ message: string; delivery: 'link' | 'on-screen'; temporaryPassword?: string }> {
    const response = await fetch((process.env.API_HOST || '') + `/api/v1/manager/reset-user-password/${userGuid}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for authentication
      body: JSON.stringify({ newPassword }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to reset user password');
    }

    return response.json();
  }

  static async uploadClientsCSV(file: File): Promise<{ successful: number; failed: number; errors: string[] }> {
    const formData = new FormData();
    formData.append('csv', file);

    const response = await fetch((process.env.API_HOST || '') + '/api/v1/manager/upload-clients', {
      method: 'POST',
      credentials: 'include', // Include cookies for authentication
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to upload CSV file');
    }

    const data = await response.json();
    return data.results;
  }

  // Court-related methods
  static async getCourts(filters?: Partial<SearchFilters>, from: number = 0, size: number = 20): Promise<{
    courts: CourtDto[];
    pagination: { from: number; size: number; total: number };
  }> {
    const queryParams = new URLSearchParams({
      from: from.toString(),
      size: size.toString(),
    });

    if (filters) {
      if (filters.search) {
        queryParams.append('search', filters.search);
      }
      if (filters.Court_Name) {
        queryParams.append('Court_Name', filters.Court_Name);
      }
      if (filters.Court_Address) {
        queryParams.append('Court_Address', filters.Court_Address);
      }
      if (filters.Court_Email) {
        queryParams.append('Court_Email', filters.Court_Email);
      }
      if (filters.Court_Phone) {
        queryParams.append('Court_Phone', filters.Court_Phone);
      }
      if (filters.Court_Fax) {
        queryParams.append('Court_Fax', filters.Court_Fax);
      }
    }

    const response = await fetch((process.env.API_HOST || '') + `/api/v1/court/list?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch courts');
    }

    const data = await response.json();
    return {
      courts: data.courts,
      pagination: data.pagination,
    };
  }

  static async getCourt(courtGuid: string): Promise<FullCourtDto> {
    const response = await fetch((process.env.API_HOST || '') + `/api/v1/court/${courtGuid}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch court');
    }

    const data = await response.json();
    return data.court;
  }

  static async createCourt(courtData: CreateCourtDto): Promise<FullCourtDto> {
    const response = await fetch((process.env.API_HOST || '') + '/api/v1/court/create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(courtData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create court');
    }

    const data = await response.json();
    return data.court;
  }

  static async updateCourt(courtGuid: string, courtData: Partial<CourtDto>): Promise<FullCourtDto> {
    const response = await fetch((process.env.API_HOST || '') + `/api/v1/court/${courtGuid}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(courtData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update court');
    }

    const data = await response.json();
    return data.court;
  }

  static async createProsecutor(prosecutorData: CreateProsecutorDto): Promise<FullProsecutorDto> {
    const response = await fetch((process.env.API_HOST || '') + '/api/v1/prosecutor/create-by-court-guid', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(prosecutorData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create prosecutor');
    }

    const data = await response.json();
    return data.prosecutor;
  }

  // Manager password reset methods
  static async requestPasswordReset(Manager_Email: string): Promise<string> {
    const response = await fetch((process.env.API_HOST || '') + '/api/v1/manager/request-password-reset', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ Manager_Email }),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || 'Failed to request password reset');
    }
    return data.message || 'Password reset email sent successfully.';
  }

  static async confirmPasswordReset(token: string, newPassword: string): Promise<void> {
    const response = await fetch(
      (process.env.API_HOST || '') + `/api/v1/manager/confirm-password-reset/${encodeURIComponent(token)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ newPassword }),
      }
    );

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || 'Failed to reset password');
    }
  }

  // Document-related methods
  static async getUserDocuments(userGuid: string): Promise<any[]> {
    const response = await fetch((process.env.API_HOST || '') + `/api/v1/manager-user/documents/${userGuid}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch user documents');
    }

    const data = await response.json();
    return data.documents || [];
  }

  static async uploadUserDocument(userGuid: string, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('document', file);

    const response = await fetch((process.env.API_HOST || '') + `/api/v1/manager-user/upload-document/${userGuid}`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to upload document');
    }

    return await response.json();
  }

  // Unmatched Calls queue — Dialpad calls that couldn't be filed automatically.
  static async getUnmatchedCalls(): Promise<UnmatchedCallDto[]> {
    const response = await fetch((process.env.API_HOST || '') + '/api/v1/unmatched-call/list', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for authentication
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({} as { message?: string; error?: string }));
      throw new ManagerApiError(response.status, errorData.error || errorData.message || 'Failed to load unmatched calls');
    }

    return response.json();
  }

  static async attachUnmatchedCall(payload: {
    UnmatchedCall_guid: string;
    User_guid: string;
    Ticket_guid?: string;
  }): Promise<AttachUnmatchedCallResult> {
    const response = await fetch((process.env.API_HOST || '') + '/api/v1/unmatched-call/attach', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for authentication
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({} as { message?: string; error?: string; errors?: Array<{ msg?: string }> }));
      // Same pattern as createUser above: "Validation failed" alone doesn't tell staff which
      // field the server rejected, so per-field messages are carried through with it.
      const fieldErrors = Array.isArray(errorData.errors)
        ? errorData.errors.map((e: { msg?: string }) => e.msg).filter(Boolean)
        : [];
      const detail = fieldErrors.length > 0 ? `: ${fieldErrors.join('; ')}` : '';
      throw new ManagerApiError(response.status, (errorData.error || errorData.message || 'Failed to attach call') + detail);
    }

    return response.json();
  }

  static async dismissUnmatchedCall(UnmatchedCall_guid: string): Promise<UnmatchedCallDto> {
    const response = await fetch((process.env.API_HOST || '') + '/api/v1/unmatched-call/dismiss', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for authentication
      body: JSON.stringify({ UnmatchedCall_guid }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({} as { message?: string; error?: string; errors?: Array<{ msg?: string }> }));
      const fieldErrors = Array.isArray(errorData.errors)
        ? errorData.errors.map((e: { msg?: string }) => e.msg).filter(Boolean)
        : [];
      const detail = fieldErrors.length > 0 ? `: ${fieldErrors.join('; ')}` : '';
      throw new ManagerApiError(response.status, (errorData.error || errorData.message || 'Failed to dismiss call') + detail);
    }

    return response.json();
  }
}

export default ManagerApi;
